import React, { useState } from "react";
import styles from "./index.module.scss";

export interface HelpCardItem {
  id: string;
  title: string;
  description?: string;
  type: string;
  fileName?: string;
  url?: string;
  order: number; // Required field for Salesforce Order__c mapping
}

interface HelpCardsProps {
  items: HelpCardItem[];
  onManage: (id: string) => void; // Changed to string
  onDelete: (id: string) => void; // Changed to string
  onReorder?: (items: HelpCardItem[]) => void;
}

const HelpCards: React.FC<HelpCardsProps> = ({ items, onManage, onDelete, onReorder }) => {
  const [draggedItem, setDraggedItem] = useState<HelpCardItem | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  const typeClass = (type: string) => (type === "PDF" ? styles.typePdf : styles.typeVideo);

  const handleDragStart = (e: React.DragEvent, item: HelpCardItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", item.id.toString());
  };

  const handleDragOver = (e: React.DragEvent, itemId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(itemId);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, targetItem: HelpCardItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDragOverItem(null);
      setDraggedItem(null);
      return;
    }

    const itemsCopy = [...items];
    const draggedIndex = itemsCopy.findIndex(item => item.id === draggedItem.id);
    const targetIndex = itemsCopy.findIndex(item => item.id === targetItem.id);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged item from its current position
      const [removed] = itemsCopy.splice(draggedIndex, 1);
      // Insert at target position
      itemsCopy.splice(targetIndex, 0, removed);

      // Update order property for all items to sync with Salesforce Order__c field
      const updatedItems = itemsCopy.map((item, index) => ({
        ...item,
        order: index + 1 // 1-based ordering for Salesforce Order__c field
      }));

      if (onReorder) {
        onReorder(updatedItems);
      }
    }

    setDragOverItem(null);
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDragOverItem(null);
    setDraggedItem(null);
  };

  if (!items || items.length === 0) {
    return (
      <div className={styles.noResultsContainer}>
        <div className={styles.noResultsText}>No Help items found</div>
      </div>
    );
  }

  return (
    <div className={styles.cardsContainer}>
      <h2 className={styles.title}>{items.length} Help items found</h2>
      {onReorder && (
        <p className={styles.dragInfo}>
          Drag and drop items to reorder them, Use the grip handle on the left to drag items.
        </p>
      )}
      {items.map((item) => (
        <div
          key={item.id}
          className={`${styles.card} ${draggedItem?.id === item.id ? styles.dragging : ''} ${dragOverItem === item.id ? styles.dragOver : ''}`}
          draggable={!!onReorder}
          onDragStart={(e) => handleDragStart(e, item)}
          onDragOver={(e) => handleDragOver(e, item.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, item)}
          onDragEnd={handleDragEnd}
        >
          <div className={styles.left} onClick={() => onManage(item.id)}>
            <div className={styles.cardContent}>
              {onReorder && (
                <div className={styles.dragHandle}>
                  <i className="fa fa-grip-vertical"></i>
                </div>
              )}
              <div className={styles.cardInfo}>
                <div className={styles.cardTitleWrapper}>
                  <div className={styles.cardTitle}>{item.title}</div>
                  <div className={`${styles.type} ${typeClass(item.type)}`}>{item.type?.toUpperCase()}</div>
                </div>
                <div className={styles.cardMeta}>
                  <div className={`${styles.description} ${!item.description ? styles.noDescription : ''}`}>
                    {item.description || 'No description'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.right}>
            <div className={styles.actions}>
              <button className={styles.primaryBtn} onClick={() => onManage(item.id)}>Manage</button>
              <button className={styles.dangerBtn} onClick={() => onDelete(item.id)}>Delete</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default HelpCards;
